import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  Users, 
  MapPin, 
  Monitor,
  CheckCircle,
  XCircle,
  User,
  RefreshCw
} from 'lucide-react';
import type { TimeSlot, ReviewSlot, Event } from '../../types';

interface ScheduleViewProps {
  isReviewerView?: boolean; // true for reviewer claiming, false for admin view
}

const ReviewerScheduleView: React.FC<ScheduleViewProps> = ({ isReviewerView = true }) => {
  const { eventId } = useParams<{ eventId: string }>();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [reviewSlots, setReviewSlots] = useState<ReviewSlot[]>([]);
  // const [isLoading, setIsLoading] = useState(false);
  const [claimingSlot, setClaimingSlot] = useState<string | null>(null);

  // Mock data - in real app, fetch from API
  const event: Event = {
    id: eventId || '1',
    title: 'Fall Portfolio Reviews',
    description: 'Comprehensive portfolio review sessions for design students',
    type: 'in-person',
    startDate: new Date('2025-09-15'),
    endDate: new Date('2025-09-17'),
    location: 'Design Building',
    maxParticipants: 100,
    currentParticipants: 45,
    isActive: true,
    reviewDuration: 20,
    breakBetweenReviews: 5,
    createdAt: new Date(),
    updatedAt: new Date(),
    createdBy: 'admin1',
    requiresApproval: false,
    isPublic: true,
    categories: [],
    tags: [],
    expectedOutcomes: [],
    requirements: [],
    scheduleConfig: {
      id: '1',
      eventId: eventId || '1',
      reviewDuration: 20,
      breakBetweenReviews: 5,
      maxConcurrentReviews: 4,
      allowSlotEditing: true,
      timezone: 'America/New_York',
      setupTimeMinutes: 15,
      cleanupTimeMinutes: 15,
      dailySchedules: []
      // rooms moved to separate management
    }
  };

  // const rooms = event.scheduleConfig?.rooms || []; // TODO: Fetch rooms separately
  const rooms: any[] = []; // Temporary - rooms managed separately now

  // Mock reviewer info (current logged in reviewer)
  const currentReviewer = {
    id: 'reviewer1',
    name: 'Jane Smith',
    expertise: ['UI/UX Design', 'Product Management'],
  };

  // Generate mock time slots for the selected date
  useEffect(() => {
    generateTimeSlots();
  }, [selectedDate]);

  const generateTimeSlots = () => {
    const slots: TimeSlot[] = [];
    const reviewSlots: ReviewSlot[] = [];
    
    // Generate slots for the selected date from 9 AM to 5 PM
    const startHour = 9;
    const endHour = 17;
    const reviewDuration = event.reviewDuration; // 20 minutes
    const breakDuration = event.breakBetweenReviews; // 5 minutes
    const slotDuration = reviewDuration + breakDuration; // 25 minutes total

    rooms.forEach(room => {
      for (let hour = startHour; hour < endHour; hour++) {
        for (let minute = 0; minute < 60; minute += slotDuration) {
          if (hour === endHour - 1 && minute + reviewDuration > 60) break; // Don't create slots that go past end time
          
          const startTime = new Date(selectedDate);
          startTime.setHours(hour, minute, 0, 0);
          
          const endTime = new Date(startTime);
          endTime.setMinutes(endTime.getMinutes() + reviewDuration);

          const slotId = `slot_${room.id}_${startTime.getTime()}`;
          
          const timeSlot: TimeSlot = {
            id: slotId,
            eventId: event.id,
            roomId: room.id,
            startTime,
            endTime,
            maxReviewers: room.capacity,
            currentReviewers: Math.floor(Math.random() * (room.capacity + 1)), // Mock current bookings
            isAvailable: true,
            createdAt: new Date(),
          };

          slots.push(timeSlot);

          // Generate review slots for this time slot
          for (let i = 0; i < room.capacity; i++) {
            const reviewSlotId = `reviewslot_${slotId}_${i}`;
            const isClaimedByCurrentUser = Math.random() < 0.1; // 10% chance current user claimed this
            const isClaimed = isClaimedByCurrentUser || Math.random() < 0.3; // 30% chance slot is claimed by someone
            
            const reviewSlot: ReviewSlot = {
              id: reviewSlotId,
              timeSlotId: slotId,
              reviewerId: isClaimed ? (isClaimedByCurrentUser ? currentReviewer.id : `reviewer${Math.floor(Math.random() * 10)}`) : undefined,
              status: isClaimed ? 'claimed' : 'available',
              claimedAt: isClaimed ? new Date(Date.now() - Math.random() * 86400000) : undefined,
            };

            reviewSlots.push(reviewSlot);
          }
        }
      }
    });

    setTimeSlots(slots);
    setReviewSlots(reviewSlots);
  };

  const getAvailableDates = () => {
    const dates: Date[] = [];
    const start = new Date(event.startDate);
    const end = new Date(event.endDate);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d));
    }
    
    return dates;
  };

  const getReviewSlotsForTimeSlot = (timeSlotId: string) => {
    return reviewSlots.filter(slot => slot.timeSlotId === timeSlotId);
  };

  const getMyClaimedSlots = () => {
    return reviewSlots.filter(slot => slot.reviewerId === currentReviewer.id);
  };

  const claimSlot = async (reviewSlotId: string) => {
    if (!isReviewerView) return;
    
    setClaimingSlot(reviewSlotId);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setReviewSlots(prev => prev.map(slot => 
        slot.id === reviewSlotId 
          ? { ...slot, reviewerId: currentReviewer.id, status: 'claimed', claimedAt: new Date() }
          : slot
      ));
      
      // Update time slot current reviewers count
      const reviewSlot = reviewSlots.find(s => s.id === reviewSlotId);
      if (reviewSlot) {
        setTimeSlots(prev => prev.map(timeSlot =>
          timeSlot.id === reviewSlot.timeSlotId
            ? { ...timeSlot, currentReviewers: timeSlot.currentReviewers + 1 }
            : timeSlot
        ));
      }
    } catch (error) {
      console.error('Error claiming slot:', error);
    } finally {
      setClaimingSlot(null);
    }
  };

  const releaseSlot = async (reviewSlotId: string) => {
    if (!isReviewerView) return;
    
    setClaimingSlot(reviewSlotId);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setReviewSlots(prev => prev.map(slot => 
        slot.id === reviewSlotId 
          ? { ...slot, reviewerId: undefined, status: 'available', claimedAt: undefined }
          : slot
      ));
      
      // Update time slot current reviewers count
      const reviewSlot = reviewSlots.find(s => s.id === reviewSlotId);
      if (reviewSlot) {
        setTimeSlots(prev => prev.map(timeSlot =>
          timeSlot.id === reviewSlot.timeSlotId
            ? { ...timeSlot, currentReviewers: Math.max(0, timeSlot.currentReviewers - 1) }
            : timeSlot
        ));
      }
    } catch (error) {
      console.error('Error releasing slot:', error);
    } finally {
      setClaimingSlot(null);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const myClaimedSlots = getMyClaimedSlots();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isReviewerView ? 'Review Schedule' : 'Schedule Management'}
            </h1>
            <p className="text-gray-600 mt-2">{event.title}</p>
          </div>
          
          {isReviewerView && (
            <div className="text-right">
              <p className="text-sm text-gray-600">Logged in as</p>
              <p className="font-medium text-gray-900">{currentReviewer.name}</p>
              <p className="text-xs text-gray-500">{currentReviewer.expertise.join(', ')}</p>
            </div>
          )}
        </div>

        {/* Event Info */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="flex items-center">
                <Calendar className="h-5 w-5 text-blue-600 mr-2" />
                <span className="text-blue-900 font-medium">
                  {event.startDate.toLocaleDateString()} - {event.endDate.toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-blue-600 mr-2" />
                <span className="text-blue-900">{event.reviewDuration} min reviews</span>
              </div>
              <div className="flex items-center">
                {event.type === 'online' ? (
                  <Monitor className="h-5 w-5 text-blue-600 mr-2" />
                ) : (
                  <MapPin className="h-5 w-5 text-blue-600 mr-2" />
                )}
                <span className="text-blue-900">{event.type === 'online' ? 'Virtual Event' : event.location}</span>
              </div>
            </div>
          </div>
        </div>

        {/* My Schedule Summary */}
        {isReviewerView && myClaimedSlots.length > 0 && (
          <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="text-lg font-medium text-green-900 mb-3 flex items-center">
              <CheckCircle className="h-5 w-5 mr-2" />
              My Claimed Slots ({myClaimedSlots.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {myClaimedSlots.map(slot => {
                const timeSlot = timeSlots.find(ts => ts.id === slot.timeSlotId);
                const room = rooms.find(r => r.id === timeSlot?.roomId);
                if (!timeSlot) return null;
                
                return (
                  <div key={slot.id} className="bg-white rounded-lg border border-green-200 p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">
                          {formatTime(timeSlot.startTime)} - {formatTime(timeSlot.endTime)}
                        </p>
                        <p className="text-sm text-gray-600">{room?.name}</p>
                        <p className="text-xs text-gray-500">{timeSlot.startTime.toLocaleDateString()}</p>
                      </div>
                      <button
                        onClick={() => releaseSlot(slot.id)}
                        disabled={claimingSlot === slot.id}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        {claimingSlot === slot.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          'Release'
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Date Selector */}
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-3">Select Date</h2>
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {getAvailableDates().map((date) => (
            <button
              key={date.toISOString()}
              onClick={() => setSelectedDate(date)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium ${
                date.toDateString() === selectedDate.toDateString()
                  ? 'bg-primary-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="text-center">
                <div>{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                <div>{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Schedule Grid */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <Clock className="h-5 w-5 mr-2" />
            Available Time Slots - {selectedDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'long', 
              day: 'numeric' 
            })}
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time
                </th>
                {rooms.map(room => (
                  <th key={room.id} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div>
                      <div className="font-medium">{room.name}</div>
                      <div className="text-xs font-normal text-gray-400">
                        {room.capacity} concurrent • {room.location}
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Object.entries(
                timeSlots
                  .filter(slot => slot.startTime.toDateString() === selectedDate.toDateString())
                  .reduce((acc, slot) => {
                    const timeKey = slot.startTime.getTime();
                    if (!acc[timeKey]) {
                      acc[timeKey] = [];
                    }
                    acc[timeKey].push(slot);
                    return acc;
                  }, {} as Record<number, TimeSlot[]>)
              )
                .sort(([a], [b]) => parseInt(a) - parseInt(b))
                .map(([timeKey, slotsAtTime]) => {
                  const time = new Date(parseInt(timeKey));
                  return (
                    <tr key={timeKey}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div>
                          <div>{formatTime(time)}</div>
                          <div className="text-xs text-gray-500">
                            {formatTime(new Date(time.getTime() + event.reviewDuration * 60000))}
                          </div>
                        </div>
                      </td>
                      {rooms.map(room => {
                        const roomSlot = slotsAtTime.find(s => s.roomId === room.id);
                        const roomReviewSlots = roomSlot ? getReviewSlotsForTimeSlot(roomSlot.id) : [];
                        
                        return (
                          <td key={room.id} className="px-6 py-4 whitespace-nowrap">
                            {roomSlot ? (
                              <div className="space-y-2">
                                {roomReviewSlots.map(reviewSlot => {
                                  const isMySlot = reviewSlot.reviewerId === currentReviewer.id;
                                  const isAvailable = reviewSlot.status === 'available';
                                  const isClaiming = claimingSlot === reviewSlot.id;
                                  
                                  return (
                                    <div
                                      key={reviewSlot.id}
                                      className={`p-2 rounded-lg border text-xs ${
                                        isMySlot
                                          ? 'bg-green-100 border-green-300'
                                          : isAvailable
                                          ? 'bg-blue-50 border-blue-200'
                                          : 'bg-gray-100 border-gray-300'
                                      }`}
                                    >
                                      {isMySlot ? (
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center">
                                            <CheckCircle className="h-3 w-3 text-green-600 mr-1" />
                                            <span className="font-medium text-green-800">Your Slot</span>
                                          </div>
                                          {isReviewerView && (
                                            <button
                                              onClick={() => releaseSlot(reviewSlot.id)}
                                              disabled={isClaiming}
                                              className="text-red-600 hover:text-red-800"
                                            >
                                              {isClaiming ? (
                                                <RefreshCw className="h-3 w-3 animate-spin" />
                                              ) : (
                                                <XCircle className="h-3 w-3" />
                                              )}
                                            </button>
                                          )}
                                        </div>
                                      ) : isAvailable ? (
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center">
                                            <User className="h-3 w-3 text-blue-600 mr-1" />
                                            <span className="text-blue-800">Available</span>
                                          </div>
                                          {isReviewerView && (
                                            <button
                                              onClick={() => claimSlot(reviewSlot.id)}
                                              disabled={isClaiming}
                                              className="text-blue-600 hover:text-blue-800"
                                            >
                                              {isClaiming ? (
                                                <RefreshCw className="h-3 w-3 animate-spin" />
                                              ) : (
                                                'Claim'
                                              )}
                                            </button>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="flex items-center">
                                          <Users className="h-3 w-3 text-gray-600 mr-1" />
                                          <span className="text-gray-700">Claimed</span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-center text-gray-400 text-xs py-2">
                                No slots
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Legend</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-green-100 border border-green-300 rounded mr-2"></div>
            <span>Your claimed slots</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-blue-50 border border-blue-200 rounded mr-2"></div>
            <span>Available to claim</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-gray-100 border border-gray-300 rounded mr-2"></div>
            <span>Claimed by others</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewerScheduleView;